import { getFBOrdersData } from "../services";
import { getFBOrderTableData } from "../services/syncServices";

/**
 * Table Status / Data Mismatch (recurring at Kompally, Secunderabad, +5 more
 * dates through 08/18) — BUG-1749's "no local IP order found for this table
 * -> reset statusType to Available (OPN), wiping wsData/items" reconciliation
 * (tables.js, useOrderHandlers.js: handleExistingOrder/cleanupCompletedOrders)
 * only ever checked the LOCAL db.fbOrderData cache. When a table's order
 * genuinely lands on a different mesh peer / the cloud but hasn't synced to
 * THIS terminal's local DB yet, that local absence is indistinguishable from
 * a truly orphaned table -- so the reconciliation destructively wiped a
 * still-running table to Available, which is exactly the reported symptom
 * (and, since wsData/items get cleared, also explains Prepare Bill then
 * showing the wrong items on reopen -- same root cause, not a second bug).
 *
 * Give the local cache one bounded chance to catch up from the server before
 * trusting its absence as proof of "orphaned". Both helpers below fail safe:
 * a timeout, an offline terminal, or any network error always resolves to
 * "don't know" rather than "orphaned", so this can only prevent false-positive
 * wipes -- it can never introduce a new destructive one. The tradeoff is that
 * a genuinely orphaned table (BUG-1749's original bug) may take one extra
 * reconciliation pass to clear if the live check is inconclusive; that is a
 * stale-display annoyance, not data loss, and is the correct side to fail on.
 */

// Bulk refresh used before a multi-table local-only reconciliation pass
// (mount / Product-Sync reload / periodic cleanup). Pulls any currently
// active (IP) orders the server knows about into local db.fbOrderData —
// getFBOrdersData() only inserts orders this terminal doesn't already have,
// so it can only add missing activity, never overwrite fresher local data.
export async function refreshLocalOrdersBeforeReconcile(timeoutMs = 4000) {
  try {
    await Promise.race([getFBOrdersData(), new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
  } catch {
    // getFBOrdersData already swallows its own errors internally; this catch
    // is just a safety net so a rejection here can never block the caller.
  }
}

// Single-table, latency-bounded live check for interactive (per-tap) call
// sites where a full bulk refresh would be too slow. Returns:
//   true  -> confirmed no active order server-side, safe to reset to OPN
//   false -> confirmed an active order exists, must NOT reset
//   null  -> inconclusive (timeout / offline / error) -- caller must NOT reset
export async function isTableGenuinelyOrphaned(cwrFbTableId, timeoutMs = 1800) {
  if (!cwrFbTableId) return null;
  try {
    const liveOrders = await Promise.race([
      getFBOrderTableData(cwrFbTableId, "IP"),
      new Promise((resolve) => setTimeout(() => resolve(undefined), timeoutMs)),
    ]);
    if (liveOrders === undefined) return null;
    return !liveOrders.some((o) => o.isCanceled !== "Y");
  } catch {
    return null;
  }
}
